// web/src/components/ArchiveFilters.tsx

export interface ArchiveFilterState {
  endAdvertisers: string[];     // account_ids
  cadence: "weekly" | "monthly" | "all";
  from: string | null;          // ISO date or null
  to: string | null;
}

interface AccountOption {
  account_id: string;
  display_name: string;
}

interface Props {
  accounts: AccountOption[];
  value: ArchiveFilterState;
  onChange: (next: ArchiveFilterState) => void;
}

export function ArchiveFilters({ accounts, value, onChange }: Props) {
  function toggleEA(accountId: string) {
    const next = value.endAdvertisers.includes(accountId)
      ? value.endAdvertisers.filter((a) => a !== accountId)
      : [...value.endAdvertisers, accountId];
    onChange({ ...value, endAdvertisers: next });
  }

  function setCadence(c: ArchiveFilterState["cadence"]) {
    onChange({ ...value, cadence: c });
  }

  function setFrom(d: string) { onChange({ ...value, from: d || null }); }
  function setTo(d: string) { onChange({ ...value, to: d || null }); }

  function clearAll() {
    onChange({ endAdvertisers: [], cadence: "all", from: null, to: null });
  }

  return (
    <div style={{
      display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center",
      padding: "12px 0", borderBottom: "1px solid rgba(255,255,255,0.06)",
    }}>
      {/* EA chips */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {accounts.map((a) => {
          const selected = value.endAdvertisers.includes(a.account_id);
          return (
            <button
              key={a.account_id}
              type="button"
              onClick={() => toggleEA(a.account_id)}
              aria-pressed={selected}
              style={{
                padding: "4px 10px", borderRadius: 12, border: "none", cursor: "pointer",
                background: selected ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.05)",
                color: selected ? "var(--color-bone)" : "var(--color-bone-dim)",
                fontSize: "var(--fs-xs)", fontFamily: "var(--font-mono)",
                minHeight: 28,
              }}
            >
              {a.display_name}
            </button>
          );
        })}
      </div>

      {/* Cadence toggle */}
      <div role="group" aria-label="Cadence" style={{ display: "inline-flex", fontSize: "var(--fs-xs)", fontFamily: "var(--font-mono)" }}>
        {(["all", "weekly", "monthly"] as const).map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCadence(c)}
            aria-pressed={value.cadence === c}
            style={{
              padding: "6px 12px", border: "none", cursor: "pointer",
              background: value.cadence === c ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.04)",
              color: value.cadence === c ? "var(--color-bone)" : "var(--color-bone-dim)",
              textTransform: "uppercase", letterSpacing: "var(--letter-label)",
            }}
          >{c === "all" ? "All" : c}</button>
        ))}
      </div>

      {/* Date range */}
      <label style={{ fontSize: "var(--fs-xs)", color: "var(--color-bone-dim)" }}>
        From&nbsp;
        <input
          type="date"
          value={value.from ?? ""}
          onChange={(e) => setFrom(e.target.value)}
          style={{ background: "rgba(255,255,255,0.05)", color: "var(--color-bone)", border: "none", padding: "4px 8px", borderRadius: 4 }}
        />
      </label>
      <label style={{ fontSize: "var(--fs-xs)", color: "var(--color-bone-dim)" }}>
        To&nbsp;
        <input
          type="date"
          value={value.to ?? ""}
          onChange={(e) => setTo(e.target.value)}
          style={{ background: "rgba(255,255,255,0.05)", color: "var(--color-bone)", border: "none", padding: "4px 8px", borderRadius: 4 }}
        />
      </label>

      {(value.endAdvertisers.length > 0 || value.cadence !== "all" || value.from || value.to) && (
        <button
          type="button"
          onClick={clearAll}
          style={{
            padding: "4px 10px", border: "none", cursor: "pointer",
            background: "transparent", color: "var(--color-bone-dim)",
            fontSize: "var(--fs-xs)", textDecoration: "underline",
          }}
        >Clear</button>
      )}
    </div>
  );
}
